/**
 * Выбранное в «Отделке» пола: какой комнате кладём покрытие, что лежит сейчас и его
 * настройки. Стоит в плашке внизу рядом с каталогом (`FinishTargetBar`), на телефоне — над
 * каталогом. Укладка на виду, а размер планки и шва, цвета, поворот и подъём свёрнуты под
 * «Настроить» и раскрываются над укладкой, вверх. Всё, что было в панели свойств справа,
 * есть и здесь, поэтому пол настраивают не уходя из каталога.
 *
 * У паркета и линолеума картинкой укладки и швов нет — их рисунок готовый. У картинки
 * свой цвет, поэтому цвет покрытия меняют только у однотонного. Каждая правка — шаг истории:
 * поле размера отдаёт значение один раз по окончании ввода, а цвет пишет шаг на нажатие.
 */
import { useState } from "react";
import {
	floorLook,
	getFloorMaterial,
	allowedFloorLayouts,
	isWholeFloorImage,
	FloorLayout,
	FloorMaterial,
	type FloorLook,
} from "@/shared/catalog";
import type { RoomFloor } from "@/shared/plan";
import { useProjectStore } from "@/shared/store";
import { useFloorsStore } from "@/shared/store/layers";
import { UIKit } from "@/shared/ui";
import { useEditorStore } from "@/widgets/editor";
import type { FloorTarget } from "../hooks/useFloorTarget";

/** Короткие подписи укладок для кнопок; полная — в подсказке. */
const LAYOUT_SHORT: Record<FloorLayout, string> = {
	[FloorLayout.Tile]: "Прямая",
	[FloorLayout.Straight]: "Палуба 1/2",
	[FloorLayout.Half]: "Палуба 1/3",
	[FloorLayout.Third]: "Палуба 1/4",
	[FloorLayout.FrenchHerringbone]: "Ёлка франц.",
	[FloorLayout.EnglishHerringbone]: "Ёлка англ.",
};

/** Как называть планку в полях: у плитки — плитка, у остальных — доска. */
const PLANK_LABEL: Record<FloorMaterial, string> = {
	[FloorMaterial.Tile]: "плитки",
	[FloorMaterial.QuartzVinyl]: "планки",
	[FloorMaterial.Laminate]: "доски",
	[FloorMaterial.Linoleum]: "рисунка",
};

/** Все настройки пола разом: стор пола меняет только переданное, остальное — из `look` */
const settingsOf = (look: FloorLook): Partial<RoomFloor> => ({
	length: look.length,
	width: look.width,
	gap: look.gap,
	tileColor: look.color,
	seamColor: look.seamColor,
	raise: look.raise,
	rotation: look.rotation,
});

export const FloorTargetCard = ({ target }: { target: FloorTarget | null }) => {
	const isReadOnly = useEditorStore((s) => s.isReadOnly);
	const pushHistory = useProjectStore((s) => s.pushHistory);
	const setRoomFloorMaterial = useFloorsStore((s) => s.setRoomFloorMaterial);
	const removeRoomFloor = useFloorsStore((s) => s.removeRoomFloor);

	const [tuning, setTuning] = useState(false);
	const floor = target?.floor;
	const look = floor ? floorLook(floor) : null;
	const texture = look?.texture;
	const editable = Boolean(target && floor && look && !isReadOnly);
	const whole = isWholeFloorImage(texture);

	/** Правка настроек пола; `history` — писать ли шаг (цвет пишет его на нажатие сам) */
	const update = (patch: Partial<RoomFloor>, history = true) => {
		if (!target || !floor || !look) return;
		if (history) pushHistory();
		const { tileColor, ...rest } = settingsOf(look);
		// Цвет картинки — её средний, в запись его не пишем: снимут картинку — вернётся свой
		setRoomFloorMaterial(target.room.id, floor.material, patch.layout ?? look.layout, {
			...rest,
			...(texture ? {} : { tileColor }),
			...patch,
		});
	};

	const material = floor?.material;
	const materialName = material === undefined ? "" : getFloorMaterial(material).name;
	const title = !target
		? "Кликните по полу или комнате"
		: !floor || !look
			? "Покрытие не выбрано"
			: (texture?.name ?? `${materialName}, однотонный`);

	const subtitle = [
		target ? target.label : "Ничего не выделено",
		look && texture ? materialName : null,
		look && !whole ? `${look.width} × ${look.length} мм` : null,
	]
		.filter(Boolean)
		.join(" · ");

	return (
		<div className="flex flex-col gap-2 p-3">
			<div className="flex items-center gap-2.5">
				<UIKit.Text
					as="span"
					className="size-10 shrink-0 overflow-hidden rounded-md border border-border bg-surface-subtle"
					style={look ? { backgroundColor: look.color } : undefined}
				>
					{texture && <img src={texture.thumb} alt="" className="size-full object-cover" />}
				</UIKit.Text>
				<div className="min-w-0 flex-1">
					<UIKit.Text className="truncate text-xs text-muted" title={subtitle}>
						{subtitle}
					</UIKit.Text>
					<UIKit.Text className="truncate text-sm font-semibold text-ink">{title}</UIKit.Text>
				</div>
				{editable && (
					<div className="flex shrink-0 gap-1">
						<button
							type="button"
							aria-expanded={tuning}
							title={whole ? "Подъём пола" : "Размер, шов, цвет, поворот, подъём"}
							className={`rounded-md border px-2 py-1 text-xs transition-colors duration-150 ${
								tuning
									? "border-accent bg-accent-soft text-accent"
									: "border-border bg-surface text-ink hover:bg-surface-hover"
							}`}
							onClick={() => setTuning(!tuning)}
						>
							Настроить
						</button>
						<button
							type="button"
							title="Снять покрытие"
							className="rounded-md border border-border bg-surface px-2 py-1 text-xs text-ink hover:bg-surface-hover"
							onClick={() => {
								if (!target) return;
								pushHistory();
								removeRoomFloor(target.room.id);
							}}
						>
							Снять
						</button>
					</div>
				)}
			</div>

			{editable && look && material !== undefined && (
				<>
					{/* Тонкие настройки свёрнуты: чаще всего выбирают картинку и укладку */}
					{tuning && (
						<div className="flex flex-col gap-1.5">
							{!whole && (
								<div className="grid grid-cols-2 gap-1.5">
									<UIKit.NumberField
										label={`Длина ${PLANK_LABEL[material]}`}
										value={look.length}
										min={50}
										step={25}
										onChange={(length) => update({ length })}
									/>
									<UIKit.NumberField
										label={`Ширина ${PLANK_LABEL[material]}`}
										value={look.width}
										min={50}
										step={25}
										onChange={(width) => update({ width })}
									/>
									<UIKit.NumberField
										label="Шов"
										value={look.gap}
										min={0}
										max={30}
										step={1}
										onChange={(gap) => update({ gap })}
									/>
									<UIKit.NumberField
										label="Поворот"
										unit="°"
										value={look.rotation}
										min={0}
										max={359}
										step={5}
										onChange={(rotation) => update({ rotation })}
									/>
								</div>
							)}
							<div className="grid grid-cols-2 gap-1.5">
								{!whole && !texture && (
									<div
										// История пишется один раз на выбор цвета, а не на каждое движение ползунка
										onPointerDown={() => pushHistory()}
									>
										<UIKit.ColorPicker
											up
											label="Цвет"
											value={look.color}
											onChange={(tileColor) => update({ tileColor }, false)}
										/>
									</div>
								)}
								{!whole && (
									<div onPointerDown={() => pushHistory()}>
										<UIKit.ColorPicker
											up
											label="Шов"
											value={look.seamColor}
											onChange={(seamColor) => update({ seamColor }, false)}
										/>
									</div>
								)}
								<UIKit.NumberField
									label="Подъём пола"
									value={look.raise}
									min={0}
									step={10}
									onChange={(raise) => update({ raise })}
								/>
							</div>
						</div>
					)}

					{/* Укладка: у готового рисунка (паркет, линолеум) её нет */}
					{!whole && (
						<div className="grid grid-cols-3 gap-1">
							{allowedFloorLayouts(material).map((layout) => (
								<button
									key={layout.id}
									type="button"
									title={layout.label}
									aria-pressed={look.layout === layout.id}
									className={`rounded-md border px-1.5 py-1 text-xs transition-colors duration-150 ${
										look.layout === layout.id
											? "border-accent bg-accent-soft font-semibold text-accent"
											: "border-border bg-surface text-ink hover:bg-surface-hover"
									}`}
									onClick={() => update({ layout: layout.id })}
								>
									{LAYOUT_SHORT[layout.id]}
								</button>
							))}
						</div>
					)}
				</>
			)}
		</div>
	);
};
